import { useCallback, useEffect, useRef, useState } from "react";
import { useSectionTracking } from "@/lib/useSectionTracking";
import { PlatformSuite, CommitmentPath } from "@/pages/sage/PlatformPitch";
import { DueDiligenceGuide } from "@/pages/sage/DueDiligence";
import { ExpertQuotes, ClientQuotes } from "@/pages/sage/ExpertQuotes";
import { SageDataPrivacy } from "@/pages/sage/DataPrivacy";
import { EngagementBuilder } from "@/pages/sage/EngagementBuilder";
import { PlatformScreens } from "@/components/PlatformScreens";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, Mail, Loader2, Send, ChevronRight, Printer,
  Sparkles, FileSignature, RefreshCw, ListChecks, ExternalLink, Phone,
  Building2, Radio, ShieldAlert, Gift, Play, MapPin, Star, Clock, Bell,
  BarChart3, Layers, Library, MessageSquare, Users, BookOpen, ShieldCheck,
  Zap, Award, AlertTriangle, Calendar,
} from "lucide-react";
import { submitSageOrder, pingSageStar } from "@/lib/api";

/* ============================================================================
 * Sage Home Loans — Tailored Microsite (cloned from the Giggle pitch, yellow/black brand)
 * Audience: Sage Home Loans leadership. All CTAs route to the in-page Order Form which
 * emails the complete selection to Colin via SendGrid.
 * Brand: Archivo · yellow #B7D3A8 / amber #B58900 · black #1C2B21
 * ========================================================================== */

const CYAN_LIGHT = "#B7D3A8";
const CYAN_DARK = "#3E6B4A";
const PINK_DARK = "#1C2B21";
const PINK_LIGHT = "#B7D3A8";
const INK = "#1C2B21";

const TRAINING_REQUEST_URL = "https://www.complisun.com/request-training-pc/";
const LAYERPATH_TOUR_URL = "https://share.layerpath.com/e/cmbo6lode0006l4077dav8qom/tour";
const REQUEST_TRAINING_VIDEO = "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/5t0ww80u_RequestingTraining.mp4";

// Hoisted so the IntersectionObserver effect can safely use [] deps.
const TRACKED_SECTIONS = [
  "suite", "diligence", "path", "build", "why", "expert", "answers", "courses", "states",
  "inside", "privacy", "tracking", "addons", "pricing", "timeline", "order",
];

export default function SageProposal() {
  const [seatCount, setSeatCount] = useState(40);
  const [starredAddons, setStarredAddons] = useState(() => new Set());
  const [viewerEmail, setViewerEmail] = useState(null);
  const { seenSections, engagementPct } = useSectionTracking(TRACKED_SECTIONS);

  useEffect(() => {
    const id = "sage-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
    const prev = document.title;
    document.title = "CompliSun · Your compliance department for Sage Home Loans";
    return () => { document.title = prev; };
  }, []);

  const handlePrint = () => window.print();
  const jumpToOrder = () => {
    const el = document.getElementById("order");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      data-testid="sage-proposal"
      style={{ background: "#fff", color: INK, fontFamily: "'Archivo', system-ui, -apple-system, sans-serif" }}
      className="min-h-screen w-full antialiased"
    >
      <style>{`
        .sg-h { font-family: 'Archivo', sans-serif; font-weight: 900; letter-spacing: -0.02em; line-height: 1.05; }
        .sg-eyebrow { font-family: 'Archivo', sans-serif; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; font-size: 11px; color: ${CYAN_DARK}; }
        .sg-grad-text { background: linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 60%, ${PINK_DARK} 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .sg-grad-bg-cyan { background: linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 100%); }
        .sg-grad-bg-pink { background: linear-gradient(135deg, #3A3A3A 0%, ${PINK_DARK} 100%); }
        .sg-cta { background: linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 100%); color: #fff; transition: transform .2s ease, box-shadow .2s ease, filter .2s ease; border-radius: 999px; font-weight: 800; }
        .sg-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -10px ${CYAN_DARK}80; filter: brightness(1.05); }
        .sg-cta-pink { background: ${PINK_DARK}; color: ${CYAN_LIGHT}; transition: transform .2s ease, box-shadow .2s ease; border-radius: 999px; font-weight: 800; }
        .sg-cta-pink:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -10px ${PINK_DARK}80; }
        .sg-card { background: #fff; border: 1.5px solid #E5EDF5; border-radius: 24px; transition: border-color .2s ease, transform .2s ease, box-shadow .2s ease; }
        .sg-card:hover { border-color: ${CYAN_LIGHT}55; box-shadow: 0 12px 32px -16px rgba(29,116,143,0.18); }
        .sg-chip { background: ${CYAN_LIGHT}1A; color: ${CYAN_DARK}; border: 1px solid ${CYAN_LIGHT}55; border-radius: 999px; padding: 6px 14px; font-size: 12px; font-weight: 700; }
        .sg-chip-pink { background: ${PINK_LIGHT}26; color: ${PINK_DARK}; border: 1px solid ${PINK_LIGHT}88; border-radius: 999px; padding: 6px 14px; font-size: 12px; font-weight: 700; }
        .anchor-target { scroll-margin-top: 96px; }
        ::-webkit-scrollbar { width: 12px; height: 12px; }
        ::-webkit-scrollbar-track { background: rgba(17,17,17,0.06) !important; }
        ::-webkit-scrollbar-thumb { background: rgba(17,17,17,0.35) !important; border-radius: 6px; border: 2px solid #fff; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(17,17,17,0.55) !important; }
        html { scrollbar-width: thin; scrollbar-color: rgba(17,17,17,0.45) rgba(17,17,17,0.06); }
        @media print {
          @page { size: Letter; margin: 0.5in; }
          [data-print="hide"], [data-testid="sage-nav"] { display: none !important; }
          .sg-card { box-shadow: none !important; border-color: #D6DBE6 !important; break-inside: avoid; }
          section { break-inside: avoid-page; padding-top: 24px !important; padding-bottom: 24px !important; }
          .sg-cta, .sg-cta-pink { background: #fff !important; color: ${INK} !important; border: 2px solid ${CYAN_DARK} !important; }
          iframe { display: none !important; }
          video { display: none !important; }
          [data-print-note] { display: block !important; }
        }
      `}</style>

      <NavBar onPrint={handlePrint} onJumpToOrder={jumpToOrder} />

      <main className="max-w-6xl mx-auto px-6 md:px-10">
        <Hero onJumpToOrder={jumpToOrder} onPrint={handlePrint} />
        <PlatformSuite />
        <ClientQuotes />
        <DueDiligenceGuide />
        <PlatformScreens
          id="inside"
          eyebrowClass="sg-eyebrow"
          headingClass="sg-h"
          cardClass="sg-card"
          accent="#3E6B4A"
          accentSpan="sg-grad-text"
          wrapClass=""
          lightbox
          eyebrow="Inside the platform · intuitive modules"
          headingText="Intuitive modules that tell your compliance story."
          headingAccentText="To regulators, bank partners, and investors."
          intro="Clear flows our experts and your team share — so compliance gets taken care of proactively, and the record proves it. When a regulator, bank partner, or investor asks how compliance works at Sage Home Loans, the platform is the answer."
          footnote="Screens shown with sample data — the live build is configured to Sage Home Loans's workflows."
        />
        <SageDataPrivacy />
        <CommitmentPath />
        <EngagementBuilder
          engagementPct={engagementPct}
          sectionsViewed={Array.from(seenSections)}
          onPrint={handlePrint}
        />
        <WhyUs />
        <ExpertQuotes />
        <ThreeAnswers />
        <CoursesOverview />
        <BuildYourOwn />
        <StateLibrary />
        <TrackingSample />
        <AddOnBundle picked={starredAddons} setPicked={setStarredAddons} viewerEmail={viewerEmail} />
        <Pricing seats={seatCount} setSeats={setSeatCount} />
        <Timeline />
        <OrderForm
          starredAddons={starredAddons}
          seats={seatCount}
          engagementPct={engagementPct}
          sectionsViewed={Array.from(seenSections)}
          onEmailChange={setViewerEmail}
        />
        <Close onJumpToOrder={jumpToOrder} />
      </main>

      <Footer />
    </div>
  );
}

/* --------------------- NAV ------------------------------------------------ */
function NavBar({ onPrint, onJumpToOrder }) {
  const items = [
    ["The suite",   "suite"],
    ["How it works", "path"],
    ["Build yours", "build"],
    ["Data privacy", "privacy"],
    ["Courses",     "courses"],
    ["Tracking",    "tracking"],
    ["Pricing",     "build"],
  ];
  return (
    <header
      data-testid="sage-nav"
      data-print="hide"
      className="sticky top-0 z-30 backdrop-blur-md"
      style={{ background: "rgba(255,255,255,0.95)", borderBottom: `1px solid ${CYAN_LIGHT}22` }}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
          <div className="sg-h text-[15px] hidden sm:block">CompliSun</div>
          <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] hidden lg:inline-block ml-2 px-2 py-1 rounded-full"
                style={{ background: `${PINK_DARK}12`, color: PINK_DARK }}>
            For Sage Home Loans
          </span>
        </div>
        <nav className="hidden md:flex items-center gap-0.5">
          {items.map(([label, id]) => (
            <a key={label} href={`#${id}`} data-testid={`nav-${id}`}
               className="text-[13px] font-bold px-2.5 py-2 rounded-full whitespace-nowrap transition-colors hover:bg-[#E5EDF5]"
               style={{ color: INK }}>
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={onPrint} data-testid="nav-print"
                  title="Download as PDF"
                  className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-bold uppercase tracking-[0.10em] border"
                  style={{ borderColor: `${CYAN_DARK}30`, color: CYAN_DARK }}>
            <Printer className="h-3.5 w-3.5" /> PDF
          </button>
          <button type="button" onClick={onJumpToOrder} data-testid="nav-order"
                  className="sg-cta-pink inline-flex items-center gap-2 h-10 px-4 text-[13px] whitespace-nowrap">
            Order form <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </header>
  );
}

/* --------------------- HERO ----------------------------------------------- */
function Hero({ onJumpToOrder, onPrint }) {
  return (
    <section className="pt-14 md:pt-20 pb-12 md:pb-16 relative">
      <div className="absolute -top-20 right-0 w-72 h-72 rounded-full opacity-20 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${PINK_LIGHT} 0%, transparent 70%)` }} />
      <div className="absolute -bottom-20 left-0 w-96 h-96 rounded-full opacity-15 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${CYAN_LIGHT} 0%, transparent 70%)` }} />

      <div className="relative">
        <div className="flex flex-col items-start gap-4 mb-6">
          <img src="/brand/prospects/sage-home-loans.png" alt="Sage Home Loans Corporation"
               className="h-14 md:h-16 object-contain" data-testid="sage-logo" />
          <span className="sg-eyebrow">A tailored proposal · prepared by CompliSun for Sage Home Loans</span>
        </div>

        <h1 className="sg-h text-5xl sm:text-6xl lg:text-7xl max-w-5xl" data-testid="hero-headline">
          Your entire compliance department. <span className="sg-grad-text">One roof. One price.</span>
        </h1>

        <p className="mt-6 text-xl leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
          A digital direct lender in 49 states touches TRID, RESPA/TILA, HMDA, ECOA fair
          lending, SAFE Act MLO licensing, FinCEN AML, and marketing/UDAAP scrutiny on every
          lead — with state examiners and investors watching. Here's the full suite: a
          <strong> fractional CCO, BSA/AML officer, risk assessment, compliance management
          system,</strong> and a training program — run by practitioners who know consumer
          fintech, multiplied by technology. Design your own engagement below and see the
          price as you build it.
        </p>

        <div className="mt-9 flex flex-wrap items-center gap-3" data-print="hide">
          <a href="#build" data-testid="hero-order-cta"
             className="sg-cta-pink inline-flex items-center gap-2 h-14 px-7 text-base">
            Design your engagement <ArrowRight className="h-4 w-4" />
          </a>
          <a href="#suite" className="sg-cta inline-flex items-center gap-2 h-14 px-7 text-base">
            See the full suite <ChevronRight className="h-4 w-4" />
          </a>
          <button type="button" onClick={onPrint} data-testid="hero-download-pdf"
                  className="inline-flex items-center gap-2 h-14 px-6 rounded-full text-base font-bold border-2"
                  style={{ borderColor: `${INK}18`, color: INK }}>
            <Printer className="h-4 w-4" /> Download PDF
          </button>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-2">
          <span className="sg-chip">$1,000/mo flat platform fee</span>
          <span className="sg-chip-pink">45-day go-live commitment</span>
          <span className="sg-chip">Exam-ready across 49 states + DC</span>
          <span className="sg-chip">SOC 2 Type II compliant</span>
          <span className="sg-chip">LendAPI Partner of the Year 2025</span>
          <span className="sg-chip">48-hour course turnaround</span>
        </div>
      </div>
    </section>
  );
}

/* --------------------- WHY COMPLISUN -------------------------------------- */
function WhyUs() {
  const pillars = [
    {
      icon: Layers,
      title: "Flexible LMS — assign & track engagement",
      blurb: "Role-based assignment, per-module knowledge checks, attestations, and examiner-ready exports. You see who completed what, when, and what they said about it.",
    },
    {
      icon: Zap,
      title: "New trainings in days, not weeks (sometimes hours)",
      blurb: "We've shipped a tailored module in under 48 hours when an examiner asked for one. Most vendors quote 4-to-6 weeks. The difference is structural — we author in-house with counsel reviewing in real time.",
    },
    {
      icon: ShieldCheck,
      title: "Real legal & compliance professionals — not e-learning generalists",
      blurb: "Every module is authored or supervised by a practicing attorney who runs live compliance programs for consumer fintechs. Content stays current because keeping it current is the day job.",
    },
    {
      icon: Award,
      title: "Strategic partner of the American Fintech Council",
      blurb: "AFC's law-firm members and policy advocates track proposed legislation at the federal and state level. We see what's coming before it lands — refund-advance scrutiny, preparer rules, state privacy laws — and we build the training before you need it.",
    },
  ];
  return (
    <section id="why" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-12">
        <div className="sg-eyebrow mb-3">Why CompliSun</div>
        <h2 className="sg-h text-4xl md:text-6xl">
          The same team <span className="sg-grad-text">stands behind every module.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="why-grid">
        {pillars.map((p) => {
          const Ic = p.icon;
          return (
            <article key={p.title} className="sg-card p-7 flex gap-5" data-testid={`why-${p.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 28)}`}>
              <div className="sg-grad-bg-cyan flex-shrink-0"
                   style={{ width: 56, height: 56, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                <Ic className="h-6 w-6" strokeWidth={2.2} />
              </div>
              <div>
                <h3 className="sg-h text-xl">{p.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed" style={{ color: INK, opacity: 0.78 }}>{p.blurb}</p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------- THREE ANSWERS -------------------------------------- */
function ThreeAnswers() {
  const items = [
    {
      n: "Q1",
      q: "Existing content or built to order?",
      a: (
        <>
          <strong>Both &mdash; that&rsquo;s the point.</strong> You get immediate access to our maintained
          library (updated <strong>monthly</strong>). On top of that we create new content tailored
          to Sage Home Loans: keyed off your own policies and procedures, so employees attest to
          <em> your</em> program, not boilerplate.
        </>
      ),
      icon: Library,
    },
    {
      n: "Q2",
      q: "How do you keep the state modules current?",
      a: (
        <>
          Through our strategic relationship with the <strong>American Fintech Council</strong>.
          AFC&rsquo;s network of law-firm members handles enforcement and case-law tracking; their
          <strong> policy advocates monitor proposed legislation</strong> at the state and federal
          level — not just enacted laws. That means we flag bills that might affect Sage Home Loans before
          they pass, and propose training before the rule lands.
        </>
      ),
      icon: RefreshCw,
    },
    {
      n: "Q3",
      q: "Can you host our or third-party content?",
      a: (
        <>
          <strong>Yes.</strong> Guardian hosts content developed by Sage Home Loans or a third party alongside
          CompliSun-authored modules, with one unified system for assignment, tracking, assessment,
          and attestation across all of it.
        </>
      ),
      icon: Users,
    },
  ];
  return (
    <section id="answers" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-12">
        <div className="sg-eyebrow mb-3">The three answers</div>
        <h2 className="sg-h text-4xl md:text-6xl">
          Built for an <span className="sg-grad-text">apples-to-apples comparison.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="three-answers-grid">
        {items.map((it) => {
          const Ic = it.icon;
          return (
            <article key={it.n} className="sg-card p-7 flex flex-col" data-testid={`answer-${it.n.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="sg-chip-pink">{it.n}</span>
                <Ic className="h-5 w-5" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
              </div>
              <h3 className="sg-h text-xl leading-snug">{it.q}</h3>
              <p className="mt-3 text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.82 }}>{it.a}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------- COURSES OVERVIEW ----------------------------------- */
function CoursesOverview() {
  const types = [
    "TRID / TILA-RESPA integrated disclosures",
    "HMDA data collection & reporting",
    "ECOA & fair lending",
    "SAFE Act & MLO licensing standards",
    "BSA/AML, OFAC, sanctions",
    "GLBA, CCPA, GDPR, data protection",
    "FCRA & fair credit reporting",
    "Bank-partner oversight & third-party risk",
    "Marketing & advertising compliance (UDAAP/Reg-Z/FTC)",
    "Servicing, collections & complaint handling",
    "Vendor / third-party oversight",
    "Workplace conduct, ethics & whistleblower",
    "Information security & phishing awareness",
    "Enterprise risk management",
    "SMART Goals & goal-setting using latest research",
    "Negotiation & business writing for compliance teams",
  ];
  return (
    <section id="courses" className="anchor-target py-14 md:py-20">
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5">
          <div className="sg-eyebrow mb-3">The catalog</div>
          <h2 className="sg-h text-4xl md:text-5xl">
            From <span className="sg-grad-text">Enterprise Risk Management</span> to
            <span className="sg-grad-text"> SMART Goals.</span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: INK, opacity: 0.78 }}>
            The current catalog spans compliance to leadership skills, and is updated monthly based
            on your needs. We have <strong>mini-courses</strong> and <strong>deep-dive courses</strong>
            {" "}(see a simple sample to the right).
          </p>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.65 }}>
            On the right is an interactive walkthrough of what a tailored course looks like inside
            the platform — click through it.
          </p>
        </div>
        <div className="col-span-12 md:col-span-7">
          <div className="sg-card overflow-hidden" data-testid="layerpath-tour">
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
              <iframe
                src={LAYERPATH_TOUR_URL}
                title="Interactive course tour"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                allowFullScreen
              />
            </div>
            <div className="p-4 flex items-center justify-between flex-wrap gap-2" style={{ borderTop: "1px solid #E5EDF5" }}>
              <div className="text-[13px]" style={{ color: INK }}>
                <strong>Interactive demo</strong> · a tailored course walkthrough
              </div>
              <a href={LAYERPATH_TOUR_URL} target="_blank" rel="noopener noreferrer"
                 className="text-[12px] font-bold inline-flex items-center gap-1" style={{ color: CYAN_DARK }}>
                Open fullscreen <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="sg-eyebrow mb-3">Course types in the catalog</div>
        <div className="flex flex-wrap gap-2" data-testid="course-type-chips">
          {types.map((t) => (
            <span key={t} className="sg-chip">{t}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------- BUILD YOUR OWN COURSE ------------------------------ */
function BuildYourOwn() {
  return (
    <section className="py-14 md:py-20">
      <div className="grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 md:col-span-7 order-2 md:order-1">
          <article className="sg-card overflow-hidden" data-testid="byo-video">
            <div className="aspect-video bg-black">
              <video src={REQUEST_TRAINING_VIDEO} controls preload="metadata" className="w-full h-full" />
            </div>
            <div className="p-4 text-[13px]" style={{ borderTop: "1px solid #E5EDF5", color: INK }}>
              <strong>Walkthrough</strong> · how your team requests a new course inside Guardian
            </div>
          </article>
        </div>
        <div className="col-span-12 md:col-span-5 order-1 md:order-2">
          <div className="sg-eyebrow mb-3">Build your own</div>
          <h2 className="sg-h text-3xl md:text-5xl">
            Need a course we don&rsquo;t have? <span className="sg-grad-text">Request it.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed" style={{ color: INK, opacity: 0.78 }}>
            Anyone on your team can submit a request from inside the platform — new state law,
            internal policy change, an incident response. The request lands with our team and goes
            straight into the authoring queue. We confirm scope, draft, and ship — typically inside
            a week, often sooner.
          </p>
          <a href={TRAINING_REQUEST_URL} target="_blank" rel="noopener noreferrer"
             className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold"
             style={{ color: PINK_DARK }}>
            See the live request form on complisun.com <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* --------------------- STATE LIBRARY (state-by-state tracker) ------------ */
function StateLibrary() {
  const STATES = [
    { code: "CA", name: "California",  note: "CRMLA · DFPI exams",        pop: 14 },
    { code: "TX", name: "Texas",       note: "SML exam cycle",            pop: 12 },
    { code: "FL", name: "Florida",     note: "OFR · MLD servicing",       pop: 11 },
    { code: "AZ", name: "Arizona",     note: "Mortgage Banker license",   pop: 6 },
    { code: "GA", name: "Georgia",     note: "d/b/a registration",        pop: 6 },
    { code: "IL", name: "Illinois",    note: "IDFPR reporting",           pop: 7 },
    { code: "WA", name: "Washington",  note: "DFI annual assessment",     pop: 5 },
    { code: "NC", name: "North Carolina", note: "Commissioner exams",     pop: 6 },
  ];
  return (
    <section id="states" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-10">
        <div className="sg-eyebrow mb-3">A living state library</div>
        <h2 className="sg-h text-4xl md:text-6xl max-w-4xl mx-auto">
          State-by-state tracking — <span className="sg-grad-text">built around you.</span>
        </h2>
        <p className="mt-4 text-lg max-w-3xl mx-auto" style={{ color: INK, opacity: 0.75 }}>
          We sit with you to map exactly how tracking should work — MLO licensing, exam cycles, state disclosure rules — across all 49 states + DC, by
          license, by which entity is exposed. From there, each state is its own maintained
          sub-module. New laws roll in without rebuilding the program.
        </p>
      </div>

      <div className="sg-card p-6 md:p-7" data-testid="state-tracker">
        <div className="grid grid-cols-12 gap-3 items-center mb-4 text-[11px] font-extrabold uppercase tracking-[0.14em]"
             style={{ color: INK, opacity: 0.6 }}>
          <div className="col-span-3 sm:col-span-2">State</div>
          <div className="col-span-3 sm:col-span-3">Tracked rule</div>
          <div className="col-span-3 sm:col-span-4">Coverage (sample)</div>
          <div className="col-span-3 sm:col-span-3 text-right">Status</div>
        </div>
        <div className="space-y-2.5">
          {STATES.map((s, idx) => {
            const widthPct = Math.min(100, (s.pop / 15) * 100);
            const isAttention = s.code === "CA" || s.code === "TX";
            return (
              <div key={s.code} className="grid grid-cols-12 gap-3 items-center" data-testid={`state-${s.code.toLowerCase()}`}>
                <div className="col-span-3 sm:col-span-2 flex items-center gap-2">
                  <span className="h-9 w-9 rounded-lg grid place-items-center sg-grad-bg-cyan text-white">
                    <span className="sg-h text-[12px]">{s.code}</span>
                  </span>
                  <span className="text-[13px] font-bold hidden sm:inline">{s.name}</span>
                </div>
                <div className="col-span-3 sm:col-span-3 text-[12px]" style={{ color: INK, opacity: 0.75 }}>
                  {s.note}
                </div>
                <div className="col-span-3 sm:col-span-4">
                  <div className="h-3 rounded-full overflow-hidden" style={{ background: `${CYAN_DARK}10` }}>
                    <div className="h-full rounded-full"
                         style={{
                           width: `${widthPct}%`,
                           background: isAttention ? `linear-gradient(90deg, ${PINK_LIGHT}, ${PINK_DARK})` : `linear-gradient(90deg, ${CYAN_LIGHT}, ${CYAN_DARK})`,
                         }} />
                  </div>
                </div>
                <div className="col-span-3 sm:col-span-3 text-right">
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full px-2.5 py-1"
                        style={{
                          background: isAttention ? `${PINK_DARK}14` : `${CYAN_DARK}10`,
                          color: isAttention ? PINK_DARK : CYAN_DARK,
                        }}>
                    {isAttention ? "Pending update" : "Tracked"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-5 text-[12px]" style={{ color: INK, opacity: 0.55 }}>
          Coverage bars and statuses on this page are illustrative. Your real dashboard will reflect
          your population, exposure, and the rules you choose to track at launch.
        </p>
      </div>
    </section>
  );
}

/* --------------------- TRACKING SAMPLE (alert + bar chart) ---------------- */
function TrackingSample() {
  const rows = [
    { name: "Avery Okonkwo",  role: "Loan Officer",   completed: 95, mins: 132, comment: "Helpful — the TRID tolerance walkthrough was clearest I\u2019ve seen." },
    { name: "Marco Ruiz",     role: "Growth",         completed: 88, mins: 118, comment: "Quiz felt fair. The mortgage-ad trigger-term case studies stuck." },
    { name: "Dana Whitfield", role: "Processing",     completed: 80, mins: 96,  comment: "Loved the scenario videos." },
    { name: "Priya Subramanian", role: "Compliance",  completed: 100, mins: 165, comment: "I want the BSA refresher quarterly, not annually." },
    { name: "Jasper Lee",     role: "Underwriting",   completed: 62, mins: 74,  comment: "Need to finish — flagged pipeline crunch last week." },
    { name: "Renee Calloway", role: "Customer Care",  completed: 90, mins: 108, comment: "Captions on the HMDA video were great." },
  ];
  const avgCompletion = Math.round(rows.reduce((s, r) => s + r.completed, 0) / rows.length);
  const totalMins = rows.reduce((s, r) => s + r.mins, 0);
  const incomplete = rows.filter((r) => r.completed < 80);

  return (
    <section id="tracking" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-10">
        <div className="sg-eyebrow mb-3 flex items-center justify-center gap-1.5">
          <BarChart3 className="h-3 w-3" /> Quarterly tracking sample
        </div>
        <h2 className="sg-h text-4xl md:text-6xl max-w-4xl mx-auto">
          See it through — <span className="sg-grad-text">with feedback loops.</span>
        </h2>
        <p className="mt-4 text-lg max-w-3xl mx-auto" style={{ color: INK, opacity: 0.75 }}>
          Each quarter we send a tracking pack: who finished, who needs a nudge, time spent per
          module, and a roll-up of what your employees told us. We pair the report with
          recommendations — refreshers to drop, modules to add, places where the team is asking for
          deeper coverage.
        </p>
      </div>

      {/* Alert card */}
      {incomplete.length > 0 && (
        <article
          className="rounded-2xl p-5 mb-5 flex items-start gap-4"
          style={{ background: `${PINK_LIGHT}14`, border: `1.5px solid ${PINK_DARK}55` }}
          data-testid="tracking-alert"
        >
          <div className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: `${PINK_DARK}1A`, color: PINK_DARK }}>
            <AlertTriangle className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold" style={{ color: PINK_DARK }}>
              Auto-alert · sent to compliance leadership &amp; the assigned manager
            </div>
            <div className="sg-h text-lg mt-1">
              {incomplete[0].name} hasn&rsquo;t finished &ldquo;TRID &amp; Fair Lending · Q2&rdquo; — completion sitting at {incomplete[0].completed}%.
            </div>
            <div className="text-[13px] mt-1.5" style={{ color: INK, opacity: 0.78 }}>
              Suggested next step: 1-click reminder + escalation reminder in 72 hours if still incomplete.
            </div>
          </div>
          <button type="button" disabled
                  className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em]"
                  style={{ background: PINK_DARK, color: "#fff" }}
                  data-testid="tracking-alert-action">
            <Bell className="h-3 w-3" /> Send reminder
          </button>
        </article>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="tracking-stats">
        <Stat label="Team" value={rows.length} suffix="learners" />
        <Stat label="Avg completion" value={`${avgCompletion}%`} />
        <Stat label="Total time spent" value={`${Math.round(totalMins / 60)}h`} suffix={`${totalMins} min`} />
        <Stat label="Needs follow-up" value={incomplete.length} suffix="below 80%" pink />
      </div>

      {/* Bar chart + recommendations tabs */}
      <TrackingTabs rows={rows} />

      <p className="mt-5 text-center text-[12px]" style={{ color: INK, opacity: 0.55 }}>
        Names and metrics on this page are illustrative. Your real reports use your roster.
      </p>
    </section>
  );
}

function TrackingTabs({ rows }) {
  const [tab, setTab] = useState("completion");
  return (
    <article className="sg-card p-6 md:p-7" data-testid="tracking-bars">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="sg-h text-xl">
          {tab === "completion" ? "TRID & Fair Lending · Q2 completion by learner" : "Quarterly recommendations from CompliSun"}
        </h3>
        <div className="inline-flex rounded-full p-1" style={{ background: "#F4F8FC", border: `1px solid ${CYAN_LIGHT}33` }}>
          <button type="button" onClick={() => setTab("completion")}
                  data-testid="tracking-tab-completion"
                  className="px-3.5 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all"
                  style={{ background: tab === "completion" ? CYAN_DARK : "transparent", color: tab === "completion" ? "#fff" : CYAN_DARK }}>
            Completion
          </button>
          <button type="button" onClick={() => setTab("recommendations")}
                  data-testid="tracking-tab-recommendations"
                  className="px-3.5 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all"
                  style={{ background: tab === "recommendations" ? PINK_DARK : "transparent", color: tab === "recommendations" ? "#fff" : PINK_DARK }}>
            Recommendations
          </button>
        </div>
      </div>

      {tab === "completion" && (
        <>
          <div className="space-y-3" data-testid="tracking-bars-list">
            {rows.map((r) => {
              const pct = r.completed;
              const alert = pct < 80;
              return (
                <div key={r.name} className="grid grid-cols-12 gap-3 items-center" data-testid={`tracking-row-${r.name.split(" ")[0].toLowerCase()}`}>
                  <div className="col-span-12 sm:col-span-3">
                    <div className="text-[13px] font-bold" style={{ color: INK }}>{r.name}</div>
                    <div className="text-[11px]" style={{ color: INK, opacity: 0.6 }}>{r.role}</div>
                  </div>
                  <div className="col-span-9 sm:col-span-6">
                    <div className="h-6 rounded-full overflow-hidden relative" style={{ background: `${CYAN_DARK}10` }}>
                      <div className="h-full rounded-full transition-all"
                           style={{
                             width: `${pct}%`,
                             background: alert ? `linear-gradient(90deg, ${PINK_LIGHT}, ${PINK_DARK})` : `linear-gradient(90deg, ${CYAN_LIGHT}, ${CYAN_DARK})`,
                           }} />
                      <span className="absolute inset-y-0 right-3 flex items-center text-[11px] font-extrabold"
                            style={{ color: pct > 35 ? "#fff" : INK }}>
                        {pct}%
                      </span>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3 text-right text-[12px] font-bold tabular-nums" style={{ color: INK, opacity: 0.7 }}>
                    {r.mins} min
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${INK}10` }}>
            <div className="sg-eyebrow mb-3 flex items-center gap-1.5">
              <MessageSquare className="h-3 w-3" /> What your employees said
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.slice(0, 4).map((r) => (
                <div key={r.name} className="rounded-xl p-3.5"
                     style={{ background: "#F4F8FC", border: `1px solid ${CYAN_LIGHT}33` }}>
                  <div className="text-[12px] font-extrabold" style={{ color: INK }}>{r.name} · {r.role}</div>
                  <div className="text-[13px] mt-1 italic" style={{ color: INK, opacity: 0.78 }}>
                    “{r.comment}”
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === "recommendations" && (
        <div data-testid="tracking-recommendations">
          <p className="text-[14px] mb-4" style={{ color: INK, opacity: 0.78 }}>
            Drafted by counsel based on Q2 completion data, employee feedback, and the regulatory
            calendar. Each item links back to the underlying signal.
          </p>
          <ul className="space-y-3">
            {[
              {
                priority: "high",
                title: "Move BSA/AML refresher to quarterly",
                body: "Priya specifically asked for it; the broader team is over-due on the placement-stage red-flag scenarios. Quarterly drumbeat aligns with the new FinCEN guidance.",
                source: "Employee feedback · Priya Subramanian",
              },
              {
                priority: "high",
                title: "Auto-escalate Jasper Lee's TRID refresher",
                body: "Sitting at 62% with one nudge already logged. We recommend a 72-hour escalation to his manager and a swap of the live segment for the 10-minute async version.",
                source: "Completion data · Jasper Lee (Engineering)",
              },
              {
                priority: "medium",
                title: "Add an HMDA data-quality sub-module before filing season",
                body: "The CFPB's latest HMDA filing guidance changed edit-check expectations. We've already drafted the module — pushes live with one click.",
                source: "AFC policy advocate alert · CFPB",
              },
              {
                priority: "medium",
                title: "Re-record the HMDA refresher with captions on by default",
                body: "Renee called out the captions specifically; analytics show 22% of viewers watched without audio.",
                source: "Employee feedback · Renee Calloway",
              },
              {
                priority: "low",
                title: "Trial the SMART Goals module with the Growth track",
                body: "Behavioural-skills modules raise overall completion rates by ~8% on average. Low-cost experiment.",
                source: "Cross-client benchmark",
              },
            ].map((rec) => (
              <li key={rec.title} className="rounded-2xl p-4"
                  style={{ background: rec.priority === "high" ? `${PINK_LIGHT}10` : "#F4F8FC",
                           border: `1px solid ${rec.priority === "high" ? PINK_DARK : CYAN_LIGHT}55` }}
                  data-testid={`recommendation-${rec.title.toLowerCase().split(" ").slice(0, 3).join("-")}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] rounded-full px-2 py-0.5"
                            style={{
                              background: rec.priority === "high" ? PINK_DARK : (rec.priority === "medium" ? CYAN_DARK : `${INK}30`),
                              color: "#fff",
                            }}>
                        {rec.priority} priority
                      </span>
                    </div>
                    <h4 className="sg-h text-base">{rec.title}</h4>
                    <p className="text-[13px] mt-1 leading-relaxed" style={{ color: INK, opacity: 0.78 }}>{rec.body}</p>
                    <div className="text-[11px] mt-2 italic" style={{ color: INK, opacity: 0.55 }}>
                      Source: {rec.source}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[12px]" style={{ color: INK, opacity: 0.55 }}>
            Recommendations land in your inbox the day after each quarter closes — and again any time
            a regulatory event materially changes the right answer.
          </p>
        </div>
      )}
    </article>
  );
}

function Stat({ label, value, suffix, pink }) {
  return (
    <div className="rounded-2xl px-4 py-3"
         style={{ background: pink ? `${PINK_LIGHT}14` : "#F4F8FC", border: `1px solid ${pink ? PINK_DARK : CYAN_LIGHT}33` }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
           style={{ color: pink ? PINK_DARK : CYAN_DARK }}>{label}</div>
      <div className="sg-h text-2xl mt-0.5">{value}</div>
      {suffix && <div className="text-[11px]" style={{ color: INK, opacity: 0.6 }}>{suffix}</div>}
    </div>
  );
}

/* --------------------- ADD-ON MODULES (star to favourite) ----------------- */
function AddOnBundle({ picked, setPicked, viewerEmail }) {
  // Debounced star-ping: collect every new star in a 3-second window, then send
  // one SendGrid alert with all the IDs.
  const pendingPings = useRef(new Set());
  const pingTimer = useRef(null);
  // viewerEmail is captured through a ref so the unmount flush always uses the
  // latest value (e.g. if the user starred first, then typed their email into
  // the order form). Without this the cleanup closes over the initial null.
  const viewerEmailRef = useRef(viewerEmail);
  useEffect(() => { viewerEmailRef.current = viewerEmail; }, [viewerEmail]);

  const flushPings = useCallback(() => {
    const ids = Array.from(pendingPings.current);
    pendingPings.current = new Set();
    if (ids.length === 0) return;
    pingSageStar(ids, viewerEmailRef.current || null);
  }, []);
  const queuePing = (id) => {
    pendingPings.current.add(id);
    if (pingTimer.current) clearTimeout(pingTimer.current);
    pingTimer.current = setTimeout(flushPings, 3000);
  };

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (pingTimer.current) {
        clearTimeout(pingTimer.current);
        flushPings();
      }
    };
  }, [flushPings]);

  const ADDONS = [
    { id: "vendor",     icon: Building2,  title: "Vendor Management",       blurb: "KYB · OFAC · ongoing attestation across your bank partner, processors, and vendors." },
    { id: "monitoring", icon: Radio,      title: "Monitoring & Testing",    blurb: "Continuous OFAC re-screens, transaction-pattern tests, attestation drift." },
    { id: "risk",       icon: ShieldAlert,title: "Risk Assessments",        blurb: "Annual + ad-hoc, with quantified findings and tracked remediation." },
    { id: "policies",   icon: BookOpen,   title: "Own Your Policies & Procedures", blurb: "Versioned P&P database mapped to obligations — you keep the keys." },
  ];
  const toggle = (id) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
        queuePing(id); // only ping on ADD (not on un-star)
      }
      return n;
    });
  };
  return (
    <section id="addons" className="anchor-target py-14 md:py-20">
      <div
        className="relative rounded-3xl p-8 md:p-12 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${CYAN_DARK} 0%, ${INK} 100%)`, color: "#fff" }}
        data-testid="addon-bundle-card"
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-25 pointer-events-none"
             style={{ background: `radial-gradient(circle, ${PINK_LIGHT} 0%, transparent 70%)` }} />

        <div className="relative flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="sg-eyebrow flex items-center gap-1.5" style={{ color: PINK_LIGHT }}>
              <Gift className="h-3 w-3" /> Grow with us · member rate after 90 days
            </div>
            <h2 className="sg-h text-3xl md:text-5xl mt-2 max-w-3xl">
              When you&rsquo;re ready, <span style={{ color: PINK_LIGHT }}>we go deeper.</span>
            </h2>
            <p className="mt-4 text-lg max-w-3xl" style={{ color: "rgba(255,255,255,0.84)" }}>
              Once Sage Home Loans is live on training, the rest of the CompliSun platform stacks on at the
              same speed and the same standard — and we train your team on every module we deploy.
              Tap the star on anything you&rsquo;d like more on.
              <strong> 20% off list</strong> after 90 days as an active training client.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.18em]"
                style={{ background: PINK_LIGHT, color: INK }}>
            20% loyalty discount
          </span>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="addon-grid">
          {ADDONS.map((a) => {
            const on = picked.has(a.id);
            const Ic = a.icon;
            return (
              <button key={a.id} type="button" onClick={() => toggle(a.id)}
                      data-testid={`addon-${a.id}`} aria-pressed={on}
                      className="text-left rounded-2xl p-5 transition-all relative"
                      style={{
                        background: on ? "#fff" : "rgba(255,255,255,0.08)",
                        border: `1.5px solid ${on ? PINK_LIGHT : "rgba(255,255,255,0.18)"}`,
                        color: on ? INK : "#fff",
                      }}>
                <div className="absolute top-3.5 right-3.5">
                  <Star
                    className="h-5 w-5 transition-all"
                    fill={on ? PINK_DARK : "transparent"}
                    style={{ color: on ? PINK_DARK : "rgba(255,255,255,0.5)" }}
                    strokeWidth={1.8}
                    data-testid={`addon-star-${a.id}`}
                  />
                </div>
                <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3"
                     style={{ background: on ? `${PINK_LIGHT}33` : "rgba(255,255,255,0.12)", color: on ? PINK_DARK : PINK_LIGHT }}>
                  <Ic className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="sg-h text-lg leading-tight" style={{ color: on ? INK : "#fff" }}>{a.title}</h3>
                <p className="text-[13px] mt-1.5 leading-relaxed"
                   style={{ color: on ? `${INK}b3` : "rgba(255,255,255,0.78)" }}>
                  {a.blurb}
                </p>
                <div className="mt-3 text-[10px] font-extrabold uppercase tracking-[0.14em]"
                     style={{ color: on ? PINK_DARK : PINK_LIGHT }}>
                  {on ? "Starred" : "Tap the star to mark interest"}
                </div>
              </button>
            );
          })}
        </div>

        {picked.size > 0 && (
          <div className="relative mt-6 rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3"
               style={{ background: "rgba(255,255,255,0.10)", border: `1.5px solid ${PINK_LIGHT}66` }}
               data-testid="addon-summary">
            <div className="text-[14px]" style={{ color: "#fff" }}>
              <strong>{picked.size} starred</strong> — included on your order form below.
            </div>
            <a href="#order" className="sg-cta-pink inline-flex items-center gap-2 h-10 px-5 text-sm whitespace-nowrap">
              See the order form <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

/* --------------------- PRICING -------------------------------------------- */
function Pricing({ seats, setSeats }) {
  const PER_SEAT = 30;
  const annual = seats * PER_SEAT;
  const onSeats = (v) => {
    const n = Math.max(1, Math.min(2000, parseInt(v, 10) || 0));
    setSeats(n);
  };
  return (
    <section id="pricing" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-12">
        <div className="sg-eyebrow mb-3">Added bonus · Training</div>
        <h2 className="sg-h text-4xl md:text-6xl">
          Training on top — <span className="sg-grad-text">$30</span> per seat, per year.
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: INK, opacity: 0.7 }}>
          The compliance department above is the engagement — this is the bonus that comes with it.
          The whole course catalog for your team, no setup fee, annual billing.
        </p>
      </div>

      <div className="sg-card p-7 md:p-9 max-w-3xl mx-auto" data-testid="pricing-card">
        <div className="flex items-end justify-center gap-3 flex-wrap">
          <span className="sg-h text-7xl sg-grad-text">$30</span>
          <div className="text-left">
            <div className="text-[15px] font-bold" style={{ color: INK }}>per learner</div>
            <div className="text-[15px]" style={{ color: INK, opacity: 0.7 }}>per year</div>
          </div>
        </div>

        <div className="mt-7 rounded-2xl p-5" style={{ background: "#F4F8FC" }}>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: CYAN_DARK }}>
                Your team size
              </label>
              <div className="flex items-center gap-3 mt-2">
                <input type="range" min="1" max="500" value={Math.min(500, seats)}
                       onChange={(e) => onSeats(e.target.value)}
                       data-testid="pricing-slider"
                       className="flex-1" style={{ accentColor: CYAN_DARK }} />
                <input type="number" min="1" max="2000" value={seats}
                       onChange={(e) => onSeats(e.target.value)}
                       data-testid="pricing-seats"
                       className="w-24 text-center rounded-full py-2 px-3 font-extrabold"
                       style={{ border: `2px solid ${CYAN_LIGHT}`, color: INK }} />
              </div>
              <div className="text-[12px] mt-1.5" style={{ color: INK, opacity: 0.55 }}>
                Pre-set to ~40 — drag to match your headcount.
              </div>
            </div>
            <div className="rounded-2xl p-5 text-right" style={{ background: "#fff", border: `2px solid ${CYAN_LIGHT}88` }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: CYAN_DARK }}>
                Your annual
              </div>
              <div className="sg-h text-4xl sg-grad-text" data-testid="pricing-annual">
                ${annual.toLocaleString()}
              </div>
              <div className="text-[12px]" style={{ color: INK, opacity: 0.65 }}>
                ~${Math.round(annual / 12).toLocaleString()}/mo equivalent
              </div>
            </div>
          </div>
        </div>

        <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            "Full counsel-authored catalog access",
            "Maintained multi-state library",
            "Per-module knowledge checks + attestation",
            "Examiner-ready reporting & export",
            "Quarterly tracking pack with recommendations",
            "Hosts your or third-party content alongside ours",
            "No setup fee · no platform configuration charge",
          ].map((b) => (
            <li key={b} className="flex items-start gap-2 text-[14px]" style={{ color: INK, opacity: 0.88 }}>
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: CYAN_DARK }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* --------------------- TIMELINE ------------------------------------------- */
function fmtShort(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Timeline() {
  const [kickoff, setKickoff] = useState(() => new Date().toISOString().slice(0, 10));
  const base = new Date(`${kickoff}T12:00:00`);
  const addDays = (n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
  const proto = addDays(14);
  const full = addDays(45);
  const fullLong = full.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <section id="timeline" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-8">
        <div className="sg-eyebrow mb-3">Timeline · yours to set</div>
        <h2 className="sg-h text-4xl md:text-6xl">
          Pick your kickoff. <span className="sg-grad-text">We handle the rest.</span>
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: INK, opacity: 0.75 }}>
          Prototype in your hands in ~2 weeks. Fully functional within 45 days — plenty of runway
          ahead of your next exam cycle. And you can start using the platform and catalog
          <strong> immediately</strong> from day one.
        </p>
        <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-3 sg-card px-5 py-4">
          <label className="text-[13px] font-bold" style={{ color: INK }} htmlFor="timeline-kickoff">
            When do you want to kick off?
          </label>
          <input
            id="timeline-kickoff"
            type="date"
            value={kickoff}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => e.target.value && setKickoff(e.target.value)}
            data-testid="timeline-kickoff"
            className="sg-input !w-auto"
          />
          <span className="text-[13px] font-bold" style={{ color: PINK_DARK }} data-testid="timeline-full-date">
            → fully functional by {fullLong}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="timeline-grid">
        {[
          { label: `Day 0 · ${fmtShort(base)}`, title: "Kickoff — use it immediately", body: "Kickoff call to confirm tracks and roster. Platform access and the existing catalog are usable from day one." },
          { label: `~2 weeks · ${fmtShort(proto)}`, title: "Prototype", body: "An Sage Home Loans-configured prototype in your hands — branding, tracks, and the first tailored modules. Free, no commitment." },
          { label: "Weeks 3–5", title: "Tailor & pilot", body: "Pilot group runs the program; Sage Home Loans-specific modules finalized; feedback rolls into polish." },
          { label: `Day 45 · ${fmtShort(full)}`, title: "Fully functional", body: "Whole team live with role-based assignment, attestation, and quarterly tracking." },
        ].map((s, i) => (
          <article key={s.label} className="sg-card p-5" data-testid={`timeline-${i}`}>
            <div className="sg-eyebrow" style={{ color: i === 3 ? PINK_DARK : CYAN_DARK }}>{s.label}</div>
            <h3 className="sg-h text-lg mt-1.5">{s.title}</h3>
            <p className="text-[13px] mt-2 leading-relaxed" style={{ color: INK, opacity: 0.78 }}>{s.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* --------------------- ORDER FORM (replaces Book the Call) ---------------- */
function OrderForm({ starredAddons, seats, engagementPct, sectionsViewed, onEmailChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tracksText, setTracksText] = useState("");
  const [readyDate, setReadyDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Propagate the email upward so the star-ping can attribute it.
  useEffect(() => {
    if (email && email.includes("@")) onEmailChange?.(email);
  }, [email, onEmailChange]);

  const ADDON_LABEL = {
    vendor:     "Vendor Management",
    monitoring: "Monitoring & Testing",
    risk:       "Risk Assessments",
    policies:   "Own Your Policies & Procedures",
  };

  // Split free-text into trimmed items (commas, newlines, or bullets).
  const parsedTracks = tracksText
    .split(/[,\n;•·]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await submitSageOrder({
        name: name.trim(),
        email: email.trim(),
        seats,
        tracks: parsedTracks,
        addons: Array.from(starredAddons || []),
        target_go_live: readyDate || null,
        notes: notes.trim() || null,
        engagement_pct: engagementPct,
        sections_viewed: sectionsViewed || [],
      });
      setDone(true);
      toast.success("Order received — Colin will follow up to confirm.");
    } catch (err) {
      toast.error("Couldn't send — email colindarke@complisun.com directly.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section id="order" className="anchor-target py-14 md:py-20">
        <div className="sg-card max-w-2xl mx-auto p-10 text-center" data-testid="order-form-done">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: CYAN_DARK }} />
          <h2 className="sg-h text-3xl">Order received.</h2>
          <p className="mt-3 text-[15px] max-w-md mx-auto" style={{ color: INK, opacity: 0.75 }}>
            Colin will confirm scope and lock the timeline — prototype in ~2 weeks, fully functional within 45 days.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="order" className="anchor-target py-14 md:py-20">
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5">
          <div className="sg-eyebrow mb-3">Order form</div>
          <h2 className="sg-h text-3xl md:text-5xl">
            Pick what you want. <span className="sg-grad-text">Send it to Colin.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed" style={{ color: INK, opacity: 0.78 }}>
            No call required. Mark the tracks you want at launch, confirm starred add-ons from
            above, and add anything else. Submitting routes a structured order report straight to
            Colin so prep can begin immediately.
          </p>
          <div className="mt-6 rounded-2xl p-4" style={{ background: "#F4F8FC", border: `1px solid ${CYAN_LIGHT}33` }}>
            <div className="sg-eyebrow mb-1.5">Your order so far</div>
            <ul className="text-[13px] space-y-1" style={{ color: INK }}>
              <li>• <strong>{seats}</strong> seats · ${(seats * 30).toLocaleString()}/year</li>
              <li>• <strong>{parsedTracks.length}</strong> track{parsedTracks.length === 1 ? "" : "s"} at launch</li>
              <li>• <strong>{(starredAddons || new Set()).size}</strong> starred add-on{(starredAddons || new Set()).size === 1 ? "" : "s"}</li>
              <li>• Engagement: <strong>{engagementPct}%</strong> of the proposal</li>
            </ul>
          </div>
        </div>

        <form onSubmit={submit} data-testid="order-form"
              className="col-span-12 md:col-span-7 sg-card p-6 md:p-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name *">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     data-testid="order-name" placeholder="Full name" className="sg-input" />
            </Field>
            <Field label="Work email *">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="order-email" placeholder="you@sagehomeloans.com" className="sg-input" />
            </Field>
          </div>

          <Field label="Tracks to launch with" hint="Free-form: roles, teams, or scenarios — separate with commas or new lines. E.g., Loan Officers · Processing · Underwriting · All Staff.">
            <textarea
              value={tracksText}
              onChange={(e) => setTracksText(e.target.value)}
              data-testid="order-tracks"
              rows={3}
              placeholder="Loan Officers, Processing, Underwriting, All Staff…"
              className="sg-input resize-none"
            />
            {parsedTracks.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="order-tracks-chips">
                {parsedTracks.map((t, i) => (
                  <span key={`${t}-${i}`} className="sg-chip text-[11px]">{t}</span>
                ))}
              </div>
            )}
          </Field>

          <Field label="Target go-live (optional)" hint="Leaving blank uses the 45-day timeline above.">
            <input type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)}
                   data-testid="order-date" className="sg-input" />
          </Field>

          <Field label="Notes for Colin">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                      data-testid="order-notes"
                      placeholder="Specific courses you want first, P&P drafts to send over, anything else."
                      className="sg-input resize-none" />
          </Field>

          {(starredAddons || new Set()).size > 0 && (
            <div className="rounded-2xl p-4" style={{ background: `${PINK_LIGHT}14`, border: `1px solid ${PINK_DARK}55` }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold mb-2" style={{ color: PINK_DARK }}>
                Starred add-ons travelling with this order
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(starredAddons).map((id) => (
                  <span key={id} className="sg-chip-pink text-[11px]">
                    <Star className="inline h-3 w-3 mr-1" fill={PINK_DARK} style={{ color: PINK_DARK }} />
                    {ADDON_LABEL[id] || id}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="order-submit"
                  className="sg-cta-pink w-full h-12 inline-flex items-center justify-center gap-2 text-base disabled:opacity-50">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending order…</>
                  : <>Send order to Colin <Send className="h-4 w-4" /></>}
          </button>
        </form>
      </div>

      <style>{`
        .sg-input { width: 100%; background: #fff; border: 1.5px solid #E5EDF5; border-radius: 12px; padding: 11px 14px; font-size: 14px; color: ${INK}; font-family: inherit; transition: border-color .15s ease, box-shadow .15s ease; }
        .sg-input:focus { outline: none; border-color: ${CYAN_LIGHT}; box-shadow: 0 0 0 4px ${CYAN_LIGHT}33; }
      `}</style>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-extrabold uppercase tracking-[0.16em] mb-1.5"
            style={{ color: INK, opacity: 0.75 }}>
        {label}
      </span>
      {children}
      {hint && (
        <span className="block text-[11px] mt-1" style={{ color: INK, opacity: 0.55 }}>{hint}</span>
      )}
    </label>
  );
}

/* --------------------- CLOSE ---------------------------------------------- */
function Close({ onJumpToOrder }) {
  return (
    <section id="close" className="anchor-target py-14 md:py-24">
      <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 60%, ${PINK_DARK} 100%)`, color: "#fff" }}
           data-testid="close-card">
        <h2 className="sg-h text-4xl md:text-6xl max-w-3xl mx-auto">
          Built around Sage Home Loans. Live in 45 days.
        </h2>
        <p className="mt-5 text-lg max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.92)" }}>
          Pick what you want from the order form. We do the rest.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button type="button" onClick={onJumpToOrder} data-testid="close-order-cta"
                  className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full text-base font-extrabold transition-transform hover:-translate-y-0.5"
                  style={{ background: "#fff", color: INK }}>
            Go to the order form <ArrowRight className="h-4 w-4" />
          </button>
          <a href="mailto:colindarke@complisun.com?subject=Sage Home Loans%20%E2%80%94%20follow-up"
             className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full text-base font-bold"
             style={{ background: "rgba(255,255,255,0.14)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.3)" }}>
            <Mail className="h-4 w-4" /> Email Colin
          </a>
        </div>
      </div>
    </section>
  );
}

/* --------------------- FOOTER --------------------------------------------- */
function Footer() {
  return (
    <footer className="mt-10 py-10 px-6 md:px-10" data-testid="sage-footer"
            style={{ background: INK, color: "#fff" }}>
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-9 w-9 object-contain" />
          <div>
            <div className="sg-h text-base">CompliSun</div>
            <div className="text-xs" style={{ opacity: 0.7 }}>Compliance Simplified</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm" style={{ opacity: 0.85 }}>
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5" style={{ color: CYAN_LIGHT }} />
            <span>1420 Washington Blvd., Suite 301<br />Detroit, MI 48226</span>
          </div>
          <a href="mailto:colindarke@complisun.com" className="flex items-start gap-2">
            <Mail className="h-4 w-4 mt-0.5" style={{ color: CYAN_LIGHT }} />
            <span>colindarke@complisun.com</span>
          </a>
          <a href="#order" className="flex items-start gap-2">
            <Calendar className="h-4 w-4 mt-0.5" style={{ color: CYAN_LIGHT }} />
            <span>Order form</span>
          </a>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-8 pt-6 text-xs leading-relaxed"
           style={{ borderTop: `1px solid rgba(255,255,255,0.15)`, opacity: 0.6 }}>
        © {new Date().getFullYear()} CompliSun · Detroit, MI. Tailored proposal for Sage Home Loans — a compliance program for a digital direct mortgage lender.
        Marketing communication subject to Michigan Rules of Professional Conduct 7.1.
      </div>
    </footer>
  );
}
